import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail, LOGO_WHITE } from "@/lib/api";
import { usePageSeo } from "@/lib/seo";

export default function Admin() {
  usePageSeo({ path: "/admin", title: "Admin | Park Communications", noindex: true });
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadSubmissions = useCallback(async () => {
    setLoadingSubs(true);
    try {
      const { data } = await api.get("/admin/submissions");
      setSubmissions(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("pc_token");
        setAuthed(false);
      }
    } finally {
      setLoadingSubs(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("pc_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then(() => { setAuthed(true); loadSubmissions(); })
      .catch(() => localStorage.removeItem("pc_token"))
      .finally(() => setChecking(false));
  }, [loadSubmissions]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("pc_token", data.token);
      setAuthed(true);
      loadSubmissions();
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("pc_token");
    setAuthed(false);
    setSubmissions([]);
  };

  if (checking) {
    return <div className="min-h-screen bg-navy flex items-center justify-center text-ivory/50 overline">Loading…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-navy grain flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md relative z-10">
          <Link to="/" className="inline-block mb-10"><img src={LOGO_WHITE} alt="Park Communications" className="h-12" /></Link>
          <p className="overline text-gold mb-3">Client Portal</p>
          <h1 className="font-serif font-light text-4xl text-ivory mb-10">Sign in</h1>
          <form onSubmit={handleLogin} data-testid="admin-login-form" className="space-y-8">
            <div className="border-b border-white/20 focus-within:border-gold transition-colors pb-2">
              <label className="overline text-ivory/50 block mb-2">Email</label>
              <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent text-ivory font-serif text-lg focus:outline-none" placeholder="admin@parkcomms.com" />
            </div>
            <div className="border-b border-white/20 focus-within:border-gold transition-colors pb-2">
              <label className="overline text-ivory/50 block mb-2">Password</label>
              <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent text-ivory font-serif text-lg focus:outline-none" placeholder="••••••••" />
            </div>
            {error && <p data-testid="admin-error" className="text-gold text-sm">{error}</p>}
            <button type="submit" data-testid="admin-login-button" disabled={loading}
              className="w-full bg-gold hover:bg-ivory disabled:opacity-50 text-navy py-4 overline transition-colors">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <Link to="/" className="overline text-ivory/40 hover:text-gold transition-colors mt-8 inline-block">&larr; Back to site</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-navy/15 bg-ivory sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-6 py-5 flex items-center justify-between">
          <div>
            <p className="overline text-gold-dark">Park Communications</p>
            <h1 className="font-serif text-2xl">Contact Submissions</h1>
          </div>
          <div className="flex items-center gap-6">
            <span data-testid="submissions-count" className="overline text-navy/50">{submissions.length} total</span>
            <button onClick={loadSubmissions} data-testid="refresh-button" className="overline text-navy/60 hover:text-navy">Refresh</button>
            <button onClick={logout} data-testid="admin-logout" className="overline text-navy bg-navy/10 hover:bg-navy hover:text-ivory px-4 py-2 transition-colors">Sign out</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10">
        {loadingSubs ? (
          <p className="text-navy/50 overline">Loading submissions…</p>
        ) : submissions.length === 0 ? (
          <div data-testid="empty-state" className="border border-navy/15 py-24 text-center">
            <p className="font-serif text-2xl text-navy/70">No submissions yet</p>
            <p className="text-navy/40 mt-2">New enquiries from the contact form will appear here.</p>
          </div>
        ) : (
          <div className="border-t border-navy/15" data-testid="submissions-table">
            <div className="hidden md:grid grid-cols-[1fr_1.2fr_1fr_0.7fr_0.5fr] gap-6 border-b border-navy/15 py-3 overline text-navy/40">
              <span>Name</span><span>Email</span><span>Company</span><span>Received</span><span>Email</span>
            </div>
            {submissions.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`submission-row-${s.id}`}
                className="w-full text-left grid md:grid-cols-[1fr_1.2fr_1fr_0.7fr_0.5fr] gap-2 md:gap-6 border-b border-navy/15 py-5 hover:bg-navy/5 transition-colors"
              >
                <span className="font-serif text-lg">{s.name}</span>
                <span className="text-navy/70 truncate">{s.email}</span>
                <span className="text-navy/60">{s.company || "—"}</span>
                <span className="text-navy/50 text-sm">{new Date(s.created_at).toLocaleDateString()}</span>
                <span className={`text-sm ${s.email_sent ? "text-green-700" : "text-navy/40"}`}>{s.email_sent ? "Sent" : "Stored"}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setSelected(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
            data-testid="submission-detail" className="bg-ivory max-w-2xl w-full p-10 border-t-2 border-gold">
            <p className="overline text-gold-dark mb-2">Enquiry</p>
            <h2 className="font-serif text-3xl mb-6">{selected.name}</h2>
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div><p className="overline text-navy/40 mb-1">Email</p><p>{selected.email}</p></div>
              <div><p className="overline text-navy/40 mb-1">Company</p><p>{selected.company || "—"}</p></div>
              <div><p className="overline text-navy/40 mb-1">Received</p><p>{new Date(selected.created_at).toLocaleString()}</p></div>
              <div><p className="overline text-navy/40 mb-1">Notification</p><p>{selected.email_sent ? "Emailed" : "Stored only"}</p></div>
            </div>
            <p className="overline text-navy/40 mb-2">Message</p>
            <p className="text-navy/80 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            <button onClick={() => setSelected(null)} data-testid="close-detail" className="mt-8 overline bg-navy text-ivory px-6 py-3 hover:bg-navy-700 transition-colors">Close</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
